import type { ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { useAuthStore, useTenantStore } from '@/stores'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarSeparator,
} from '@/components/ui/sidebar'
import { useNavigate, useLocation } from 'react-router-dom'
import { 
  Home, 
  Building, 
  Users, 
  MessageSquare, 
  Settings,
  LogOut,
  Key,
  UserCog,
  Grid3x3,
  ExternalLink,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Identicon } from '@/components/Identicon'
import WhiteLogo from '@/assets/hogeadmin.logo.white.svg?react'

interface MainLayoutProps {
  children: ReactNode
}

const MainLayout = observer(({ children }: MainLayoutProps) => {
  const authStore = useAuthStore()
  const tenantStore = useTenantStore()
  const navigate = useNavigate()
  const location = useLocation()
  const tenantId = authStore.currentTenantId

  const hogeSeriesProducts = [
    {
      name: 'hogeAdmin',
      url: import.meta.env.VITE_HOGE_ADMIN_URL,
      icon: UserCog,
      description: 'テナント管理',
      current: true,
    },
    {
      name: 'hogeCast',
      url: `${import.meta.env.VITE_HOGE_CAST_URL}?spoofingTenantId=${authStore.isSpoofing ? authStore.currentTenantId : ''}`,
      icon: MessageSquare,
      description: 'セグメント配信',
      current: false,
    },
    {
      name: 'hogeAgent',
      url: `${import.meta.env.VITE_HOGE_AGENT_URL}?spoofingTenantId=${authStore.isSpoofing ? authStore.currentTenantId : ''}`,
      icon: Users,
      description: 'リアルタイムチャット',
      current: false,
    },
  ]

  // なりすましモード中は通常ユーザーのメニューを表示
  const isRootMode = authStore.isRootTenant && !authStore.isSpoofing
  
  const menuItems = [
    {
      label: 'ダッシュボード',
      icon: Home,
      path: '/',
      show: true,
    },
    {
      label: 'テナント管理',
      icon: Building,
      path: '/tenants',
      // disableRootTenant: false → rootユーザーのみ表示
      show: isRootMode,
    },
    {
      label: 'テナント情報',
      icon: Building,
      path: `/tenants/${tenantId}`,
      // disableRootTenant: true → 通常ユーザーのみ表示
      show: !isRootMode && tenantId,
    },
    {
      label: 'ルーム管理',
      icon: MessageSquare,
      path: `/tenants/${tenantId}/rooms`,
      // disableRootTenant: true → 通常ユーザーのみ表示
      show: !isRootMode && tenantId,
    },
    {
      label: 'ゲスト管理',
      icon: Users,
      path: `/tenants/${tenantId}/guests`,
      // disableRootTenant: true → 通常ユーザーのみ表示
      show: !isRootMode && tenantId,
    },
  ]

  const settingsItems = [
    {
      label: 'LINE設定',
      icon: Settings,
      path: `/tenants/${tenantId}/settings/line`,
      // disableRootTenant: true → 通常ユーザーのみ表示
      show: !isRootMode && tenantId,
    },
    {
      label: 'ユーザー管理',
      icon: UserCog,
      path: `/tenants/${tenantId}/users`,
      // disableRootTenantがない → 両方に表示
      show: tenantId,
    },
    {
      label: 'APIキー',
      icon: Key,
      path: `/tenants/${tenantId}/keys`,
      // disableRootTenantがない → 両方に表示
      show: tenantId,
    },
  ]

  const handleLogout = () => {
    authStore.logout()
    navigate('/auth/login')
  }

  console.log(WhiteLogo);

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <Sidebar>
        <div className="flex items-center h-[53px] bg-[#2f3f69]">
          <WhiteLogo height={28} />
        </div>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>メニュー</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {menuItems.filter(item => item.show).map((item) => (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        isActive={location.pathname === item.path}
                        onClick={() => navigate(item.path)}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {settingsItems.some(item => item.show) && (
              <SidebarGroup>
                <SidebarGroupLabel>設定</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {settingsItems.filter(item => item.show).map((item) => (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          isActive={location.pathname === item.path}
                          onClick={() => navigate(item.path)}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            <div className="mt-auto">
              {authStore.isSpoofing && (
                <>
                  <SidebarSeparator />
                  <div className="bg-destructive text-destructive-foreground p-3">
                    <div className="text-sm mb-2 text-white">
                      なりすまし管理中: {tenantStore.currentTenant?.name} ({authStore.currentTenantId})
                    </div>
                    <Button 
                      size="sm" 
                      variant="secondary"
                      className="w-full"
                      onClick={async () => {
                        const originalTenantId = authStore.originalTenantId
                        authStore.endSpoofing()
                        // 元のテナント情報を再取得
                        if (originalTenantId) {
                          await tenantStore.getTenant(originalTenantId)
                        }
                        navigate('/')
                      }}
                    >
                      終了
                    </Button>
                  </div>
                </>
              )}
              
              <SidebarSeparator />
              
              <SidebarGroup>
                <SidebarGroupContent>
                  <div className="flex items-center gap-3 p-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback>
                        <Identicon source={authStore.user?.email || ''} size={40} />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {authStore.user?.name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {authStore.user?.email}
                      </div>
                    </div>
                  </div>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton onClick={handleLogout}>
                        <LogOut className="h-4 w-4" />
                        <span>ログアウト</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </div>
          </SidebarContent>
        </Sidebar>

        <div className="flex-1 flex flex-col">
          <header className="border-b px-4 py-3 flex items-center justify-between">
            <SidebarTrigger />
            
            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Grid3x3 className="h-4 w-4" />
                    <span className="hidden sm:inline">hogeSeries</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>プロダクト一覧</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {hogeSeriesProducts.map((product) => (
                    <DropdownMenuItem
                      key={product.name}
                      disabled={product.current}
                      onClick={() => window.location.href = product.url}
                      className="cursor-pointer"
                    >
                      <product.icon className="mr-2 h-4 w-4" />
                      <div className="flex-1">
                        <div className="font-medium">{product.name}</div>
                        <div className="text-xs text-muted-foreground">{product.description}</div>
                      </div>
                      {!product.current && <ExternalLink className="ml-2 h-3 w-3" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="flex-1 overflow-auto p-6">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  )
})

export default MainLayout